/**
 * <christmas-carousel>: the design carousel in the "Build your Christmas swag here!" feature
 * (snippets/christmas-swag.liquid). A plain custom element with no imports.
 *
 * The slides are a row that scrolls sideways with snap points, so swiping and scrolling are
 * the browser's own and work without this script. The script adds previous/next buttons,
 * one dot per slide, and a slow autoplay that moves one slide at a time. Autoplay stops while
 * the pointer is over the carousel or focus is inside it, while the page is hidden, after the
 * visitor uses the controls or swipes, and when the pause button is pressed. With "reduce
 * motion" asked for, there is no autoplay at all.
 */

class ChristmasCarousel extends HTMLElement {
  #abort = new AbortController();
  /** @type {number | undefined} */
  #timer;
  /** @type {number | undefined} */
  #scrollTimer;
  #hovered = false;
  #focused = false;
  #stopped = false;
  #current = 0;

  connectedCallback() {
    const track = this.#track();
    const slides = this.#slides();
    if (!track || slides.length === 0) return;
    const { signal } = this.#abort;

    const controls = this.querySelector('[data-controls]');
    if (controls instanceof HTMLElement && slides.length > 1) controls.hidden = false;
    this.#buildDots();

    this.querySelector('[data-previous]')?.addEventListener('click', () => this.#manual(this.#current - 1), { signal });
    this.querySelector('[data-next]')?.addEventListener('click', () => this.#manual(this.#current + 1), { signal });
    this.querySelector('[data-toggle]')?.addEventListener('click', () => this.#toggle(), { signal });
    this.addEventListener('pointerenter', () => this.#pause('hover', true), { signal });
    this.addEventListener('pointerleave', () => this.#pause('hover', false), { signal });
    this.addEventListener('focusin', () => this.#pause('focus', true), { signal });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(/** @type {Node | null} */ (event.relatedTarget))) this.#pause('focus', false);
    }, { signal });
    // A swipe or drag on the track is the visitor taking over.
    track.addEventListener('pointerdown', () => this.#stop(), { signal });
    track.addEventListener('wheel', () => this.#stop(), { signal, passive: true });
    track.addEventListener('scroll', () => {
      window.clearTimeout(this.#scrollTimer);
      this.#scrollTimer = window.setTimeout(() => this.#findCurrent(), 80);
    }, { signal, passive: true });
    document.addEventListener('visibilitychange', () => this.#schedule(), { signal });

    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    motion.addEventListener('change', () => this.#schedule(), { signal });
    const toggle = this.querySelector('[data-toggle]');
    if (toggle instanceof HTMLElement) toggle.hidden = slides.length < 2 || motion.matches;
    this.#showCurrent();
    this.#schedule();
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#abort = new AbortController();
    window.clearInterval(this.#timer);
    window.clearTimeout(this.#scrollTimer);
    this.#timer = undefined;
  }

  #track() {
    const track = this.querySelector('[data-track]');
    return track instanceof HTMLElement ? track : null;
  }

  /** @returns {HTMLElement[]} */
  #slides() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-slide]')]);
  }

  #buildDots() {
    const dots = this.querySelector('[data-dots]');
    if (!dots) return;
    const label = this.dataset.slideLabel || '[n]';
    const slides = this.#slides();
    dots.replaceChildren(
      ...slides.map((slide, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'xmas-swag__dot';
        const name = slide.querySelector('img')?.alt || '';
        dot.setAttribute('aria-label', label.replace('[n]', String(index + 1)).replace('[total]', String(slides.length)).replace('[name]', name));
        dot.addEventListener('click', () => this.#manual(index), { signal: this.#abort.signal });
        return dot;
      })
    );
  }

  /** @param {number} index */
  #go(index) {
    const track = this.#track();
    const slides = this.#slides();
    if (!track || slides.length === 0) return;
    this.#current = (index + slides.length) % slides.length;
    const slide = slides[this.#current];
    // Physical pixels, so it works the same left to right and right to left.
    const rtl = getComputedStyle(track).direction === 'rtl';
    const trackBox = track.getBoundingClientRect();
    const slideBox = slide.getBoundingClientRect();
    const delta = rtl ? slideBox.right - trackBox.right : slideBox.left - trackBox.left;
    track.scrollBy({ left: delta });
    this.#showCurrent();
  }

  /** @param {number} index */
  #manual(index) {
    this.#stop();
    this.#go(index);
  }

  #atEnd() {
    const track = this.#track();
    return !!track && Math.abs(track.scrollLeft) + track.clientWidth >= track.scrollWidth - 2;
  }

  // The slide whose start is nearest the start of the track.
  #findCurrent() {
    const track = this.#track();
    if (!track) return;
    const rtl = getComputedStyle(track).direction === 'rtl';
    const trackBox = track.getBoundingClientRect();
    let best = 0;
    let bestDistance = Infinity;
    this.#slides().forEach((slide, index) => {
      const box = slide.getBoundingClientRect();
      const distance = Math.abs(rtl ? box.right - trackBox.right : box.left - trackBox.left);
      if (distance < bestDistance) {
        best = index;
        bestDistance = distance;
      }
    });
    // At the end of the row the last slides cannot reach the start; count it as the last one,
    // so the next step goes back to the first.
    if (this.#atEnd()) best = this.#slides().length - 1;
    this.#current = best;
    this.#showCurrent();
  }

  #showCurrent() {
    this.querySelectorAll('.xmas-swag__dot').forEach((dot, index) => {
      if (index === this.#current) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  /** @param {'hover' | 'focus'} reason @param {boolean} on */
  #pause(reason, on) {
    if (reason === 'hover') this.#hovered = on;
    else this.#focused = on;
    this.#schedule();
  }

  #stop() {
    this.#stopped = true;
    this.#showToggle();
    this.#schedule();
  }

  #toggle() {
    this.#stopped = !this.#stopped;
    this.#showToggle();
    this.#schedule();
  }

  #showToggle() {
    const toggle = this.querySelector('[data-toggle]');
    if (toggle) toggle.textContent = (this.#stopped ? this.dataset.play : this.dataset.pause) || '';
  }

  // Runs the autoplay only while nothing asks it to wait.
  #schedule() {
    window.clearInterval(this.#timer);
    this.#timer = undefined;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || this.#stopped || this.#hovered || this.#focused || document.hidden || this.#slides().length < 2) return;
    const seconds = Math.max(3, Number(this.dataset.seconds) || 5);
    this.#timer = window.setInterval(() => this.#go(this.#current + 1), seconds * 1000);
  }
}

if (!customElements.get('christmas-carousel')) customElements.define('christmas-carousel', ChristmasCarousel);
